import React, { useState } from 'react';
import { Mail, Phone, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://motoring-daily.onrender.com';

const getApiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((item) => item?.msg || JSON.stringify(item)).join(' | ');
  }
  return fallback;
};

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Mobile validation (optional but if provided, must be valid)
    if (formData.mobile.trim() && !/^[0-9]{10,15}$/.test(formData.mobile.replace(/[\s\-\(\)]/g, ''))) {
      newErrors.mobile = 'Please enter a valid mobile number (10-15 digits)';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/contact`, {
        ...formData,
        subject: formData.subject.trim() || 'General enquiry'
      });
      setShowSuccess(true);
      setFormData({
        name: '',
        email: '',
        mobile: '',
        subject: '',
        message: ''
      });
      setErrors({});
    } catch (error) {
      alert(getApiErrorMessage(error, 'Failed to send message. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black mb-6 font-heading text-center">
            Get In Touch
          </h1>
          <p className="text-lg leading-relaxed text-[#666666] mb-12 text-center">
            Have questions or feedback? We'd love to hear from you.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-6 text-center border border-[#E5E7EB]">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#00A859] text-white rounded-full mb-4">
                <Mail className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold mb-2 font-heading">Email</h3>
              <p className="text-[#666666]">info@motoringdaily.com</p>
            </div>
            <div className="bg-white p-6 text-center border border-[#E5E7EB]">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#00A859] text-white rounded-full mb-4">
                <Phone className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold mb-2 font-heading">Phone</h3>
              <p className="text-[#666666]">+91 95349 63498</p>
            </div>
          </div>

          <div className="bg-white p-8 border border-[#E5E7EB]">
            <h2 className="text-2xl font-bold mb-6 font-heading">Send us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 uppercase tracking-wide">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`bg-[#F8F9FA] border rounded-none px-4 py-3 text-[#1A1A1A] w-full transition-colors ${
                      errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-[#E5E7EB] focus:border-[#00A859] focus:ring-[#00A859]'
                    }`}
                    data-testid="contact-name-input"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 uppercase tracking-wide">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`bg-[#F8F9FA] border rounded-none px-4 py-3 text-[#1A1A1A] w-full transition-colors ${
                      errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-[#E5E7EB] focus:border-[#00A859] focus:ring-[#00A859]'
                    }`}
                    data-testid="contact-email-input"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 uppercase tracking-wide">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    placeholder="e.g., +91 9876543210"
                    className={`bg-[#F8F9FA] border rounded-none px-4 py-3 text-[#1A1A1A] w-full transition-colors ${
                      errors.mobile ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-[#E5E7EB] focus:border-[#00A859] focus:ring-[#00A859]'
                    }`}
                    data-testid="contact-mobile-input"
                  />
                  {errors.mobile && <p className="text-red-500 text-sm mt-1">{errors.mobile}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 uppercase tracking-wide">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="bg-[#F8F9FA] border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859] rounded-none px-4 py-3 text-[#1A1A1A] w-full transition-colors"
                    data-testid="contact-subject-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 uppercase tracking-wide">
                  Message *
                </label>
                <textarea
                  rows="6"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className={`bg-[#F8F9FA] border rounded-none px-4 py-3 text-[#1A1A1A] w-full transition-colors ${
                    errors.message ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-[#E5E7EB] focus:border-[#00A859] focus:ring-[#00A859]'
                  }`}
                  data-testid="contact-message-input"
                />
                {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#00A859] hover:bg-[#008a49] disabled:bg-gray-400 text-white font-bold uppercase tracking-wider py-3 px-6 rounded-none transition-colors duration-200"
                data-testid="contact-submit-btn"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full mx-4 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#00A859] text-white rounded-full mb-4">
              <CheckCircle className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold mb-2 font-heading text-[#1A1A1A]">Success!</h3>
            <p className="text-[#666666] mb-6">Your message has been sent successfully</p>
            <button
              onClick={() => setShowSuccess(false)}
              className="bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-2 px-6 rounded-none transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};